import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { motion, AnimatePresence } from 'framer-motion';

// Define package options
const packages = [
  {
    id: 'basic',
    name: 'Standard Portrait',
    price: '25',
    description: 'Perfect for beginners',
    features: [
      'Single person portrait',
      'Basic conversation abilities',
      'Free software updates'
    ],
    color: 'purple'
  },
  {
    id: 'premium',
    name: 'Family Portrait',
    price: '35',
    description: 'Our most popular option',
    features: [
      'Up to 2 family members',
      'Advanced conversation',
      'Premium frame designs',
    ],
    color: 'purple',
    isPopular: true
  },
  {
    id: 'ultimate',
    name: 'Headmaster\'s Portrait',
    price: '67',
    description: 'For the serious wizard',
    features: [
      'Multiple portraits (up to 6)',
      'Premium AI capabilities',
      'Priority customer support',
    ],
    color: 'purple'
  }
];

export default function PricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // For demonstration, we'll use a mock user state
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectionStep, setSelectionStep] = useState('select'); // 'select', 'confirm', 'processing'
  
  // Handle preselected package from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const preselect = searchParams.get('preselect');
    if (preselect) {
      const packageToSelect = packages.find(pkg => pkg.id === preselect);
      if (packageToSelect) {
        setSelectedPackage(packageToSelect);
        setSelectionStep('confirm');
      }
    }
  }, [location.search]);
  
  // Reset selection step if package changes
  useEffect(() => {
    if (selectedPackage) {
      setSelectionStep('confirm');
    } else {
      setSelectionStep('select');
    }
  }, [selectedPackage]);

  const handleSelectPackage = (pkg) => {
    // If already selected, unselect it
    if (selectedPackage?.id === pkg.id) {
      setSelectedPackage(null);
      return;
    }
    
    // Add a slight delay for better UX
    setSelectionStep('processing');
    setTimeout(() => {
      setSelectedPackage(pkg);
      setSelectionStep('confirm');
    }, 300);
  };

  const handleProceedToCheckout = () => {
    if (!selectedPackage) return;
    
    // Visual feedback before navigation
    setSelectionStep('processing');
    
    // Add package info to URL params
    const params = new URLSearchParams({
      package: selectedPackage.id,
      price: selectedPackage.price,
      name: selectedPackage.name
    });
    
    // Short delay for animation
    setTimeout(() => {
      // In a real implementation with Clerk, we would use the ClerkProvider's 
      // isSignedIn state to check authentication. For now, we'll always go to checkout
      // since we're simulating a signed-in user there.
      navigate(`/checkout?${params.toString()}`);
      
      // With real Clerk integration, we would handle it like this:
      /*
      if (isSignedIn) {
        navigate(`/checkout?${params.toString()}`);
      } else {
        // For a modal approach, we would trigger Clerk's modal here
        // Instead of redirecting, we would use SignInButton's modal approach
        openSignInModal({ redirectUrl: `/checkout?${params.toString()}` });
      }
      */
    }, 500);
  };

  return (
    <div className="min-h-screen">
      <Navbar /> 
      <main className="pricing">
        <div>
          <h1 className="section-title">Choose Your Wizard Portrait Plan</h1>
          <p className="subtitle">
            Transform your television into an enchanted living portrait with our magical options
          </p>
          
          {/* Progress indicators */}
          <div className="progress-container">
            <div className="progress-steps">
              <div className="progress-step">
                <div className="step-circle">1</div>
                <span className="step-label">Choose Plan</span>
              </div>
              <div className="progress-line"></div>
              <div className="progress-step">
                <div className="step-circle">2</div>
                <span className="step-label">Review</span>
              </div>
              <div className="progress-line"></div>
              <div className="progress-step">
                <div className="step-circle">3</div>
                <span className="step-label">Checkout</span>
              </div>
            </div>
          </div>


          {selectedPackage && selectionStep === 'confirm' && (
            <div className="selection-summary">
              <h2 className="summary-title">Your Selection</h2>
              <div className="summary-content">
                <div>
                  <h3 className="summary-package">{selectedPackage.name}</h3>
                  <p className="summary-description">{selectedPackage.description}</p>
                  <p className="summary-message">Ready to transform your space with your magical wizard portrait?</p>
                </div>
                <div className="summary-price">${selectedPackage.price}</div>
              </div>
              
              <div className="summary-actions">
                <button
                  onClick={() => setSelectedPackage(null)}
                  className="change-selection"
                >
                  Change Selection
                </button>
                
                <button
                  onClick={handleProceedToCheckout}
                  disabled={selectionStep === 'processing'}
                  className={`checkout-button ${selectionStep === 'processing' ? 'processing' : ''}`}
                >
                  {selectionStep === 'processing' ? 'Processing...' : 'PROCEED TO CHECKOUT'}
                </button>
              </div>
            </div>
          )}


          <div className="pricing-container">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`pricing-card ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                onClick={() => handleSelectPackage(pkg)}
              >
                {pkg.isPopular && <div className="popular-tag">Popular</div>}
                
                <h2 className="pricing-title">{pkg.name}</h2>
                
                <div className="price">
                  ${pkg.price} 
                </div>
                <span style={{ fontSize: '0.7rem', opacity: '0.7', fontWeight: '400' }}>per month, billed annually</span>
                <ul className="feature-list">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="feature-item">
                      {feature}
                    </li>
                  ))}
                </ul>
                
                {selectedPackage?.id === pkg.id ? (
                  <div className="selected-label">
                    SELECTED
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectPackage(pkg);
                    }}
                    className="cta-button"
                  >
                    SELECT PACKAGE
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <style jsx>{`
        .pricing {
          background: var(--light, #f9f9f9);
          color: var(--text-dark, #333);
          padding: 8rem 2rem;
          text-align: center;
        }
        
        .section-title {
          font-family: var(--serif, serif);
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 1rem;
          position: relative;
          display: inline-block;
          font-weight: 600;
        }
        
        .section-title::after {
          content: '';
          position: absolute;
          width: 80px;
          height: 4px;
          background: var(--accent, #F9B932);
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .subtitle {
          font-size: 1.2rem;
          max-width: 700px;
          margin: 2rem auto 3rem;
        }
        
        .progress-container {
          margin: 3rem auto;
          max-width: 600px;
        }
        
        .progress-steps {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--primary, #6A3EA1);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        .step-label {
          margin-top: 8px;
          font-size: 0.9rem;
        }
        
        .progress-line {
          flex: 1;
          height: 2px;
          background: var(--secondary, #9857D3);
          margin: 0 10px;
        }
        
        .pricing-container {
          max-width: 1200px;
          margin: 4rem auto 0;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }
        
        .pricing-card {
          background: white;
          border-radius: 15px;
          padding: 3rem 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
          z-index: 1;
          cursor: pointer;
        }
        
        .pricing-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 0;
          background: linear-gradient(135deg, var(--primary, #6A3EA1) 0%, var(--secondary, #9857D3) 100%);
          z-index: -1;
          transition: all 0.3s ease;
        }
        
        .pricing-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          color: var(--text-light, #fff);
        }
        
        .pricing-card:hover::before {
          height: 100%;
        }
        
        .pricing-card.selected {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          border: 2px solid var(--accent, #F9B932);
        }
        
        .pricing-card:hover .price {
          color: var(--accent, #F9B932);
        }
        
        .pricing-card:hover .feature-item {
          border-color: rgba(255, 255, 255, 0.1);
        }
        
        .popular-tag {
          position: absolute;
          top: 20px;
          right: -30px;
          background: var(--accent, #F9B932);
          color: var(--text-dark, #333);
          padding: 0.5rem 3rem;
          font-size: 0.8rem;
          font-weight: 600;
          transform: rotate(45deg);
        }
        
        .pricing-title {
          font-size: 1.8rem;
          margin-bottom: 1.5rem;
          font-family: var(--serif, serif);
        }
        
        .price {
          font-size: 3.5rem;
          font-weight: 700;
          color: var(--primary, #6A3EA1);
          transition: all 0.3s ease;
        }
        
        .price span {
          font-size: 1rem;
          opacity: 0.7;
          font-weight: 400;
        }
        
        .feature-list {
          margin-bottom: 2rem;
          list-style: none;
          padding: 0;
        }
        
        .feature-item {
          padding: 1rem 0;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }
        
        .feature-item:last-child {
          border-bottom: none;
        }
        
        .cta-button {
          background: var(--accent, #F9B932);
          color: var(--text-dark, #333);
          font-size: 1rem;
          font-weight: 600;
          padding: 0.8rem 1.8rem;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: inline-block;
          width: auto;
        }
        
        .cta-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(249, 185, 50, 0.3);
        }
        
        .selected-label {
          background: var(--primary, #6A3EA1);
          color: white;
          text-align: center;
          font-weight: bold;
          padding: 0.8rem 0;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .selection-summary {
          max-width: 800px;
          margin: 3rem auto 0;
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }
        
        .summary-title {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 1.5rem;
          text-align: left;
          color: var(--text-dark, #333);
        }
        
        .summary-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-align: left;
        }
        
        .summary-package {
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .summary-description {
          color: var(--text-muted, #666);
          margin-bottom: 1rem;
        }
        
        .summary-message {
          margin-top: 1rem;
        }
        
        .summary-price {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--primary, #6A3EA1);
        }
        
        .summary-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 2rem;
        }
        
        .change-selection {
          color: var(--primary, #6A3EA1);
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 500;
        }
        
        .change-selection:hover {
          text-decoration: underline;
        }
        
        .checkout-button {
          background: var(--accent, #F9B932);
          color: var(--text-dark, #333);
          font-size: 1rem;
          font-weight: 600;
          padding: 0.8rem 1.8rem;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .checkout-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(249, 185, 50, 0.3);
        }
        
        .checkout-button.processing {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        @media (max-width: 768px) {
          .pricing-container {
            grid-template-columns: 1fr;
          }
          
          .summary-content {
            flex-direction: column;
            text-align: center;
          }
          
          .summary-price {
            margin-top: 1rem;
          }
          
          .summary-actions {
            flex-direction: column;
            gap: 1rem;
          }
          
          .section-title {
            font-size: 2rem;
          }
        }
      `}</style>
      
      <Footer />
    </div>
  );
} 